/**
 * ENHANCED EFFECTS: the fire, smoke and hit sparks a burning or freshly-hit
 * **aeroplane** wears when the port-extension toggle is on — and, since the
 * trains lane, the coal plume a **working train** trails (`collectTrainPlumes`
 * in `frame-signals.ts`; a train carries `plumeIntensity` instead of the damage
 * ladder, never burns here, and never sparks).
 *
 * The 1990 picture is a held `EXPLO.BMP` billboard flickering between its two
 * hot states plus a dithered `SMOKE.BMP` puff every 0.42 s
 * (`sprite-effects.ts`, `smoke-trail.ts`). That is what the original drew and
 * it is what the toggle's **off** state still draws, byte for byte — this file
 * is never on the path when `enhancedEffects` is false, and the sprite path
 * gets the identical source lists it always got (`frame-signals.ts`'s
 * `FrameSourceOptions`). With the toggle **on** the same states drive particles
 * instead, and the sprite path is told to skip aeroplanes — and the working
 * train's plume — so nothing is drawn twice.
 *
 * ## What it costs
 *
 * **Two draw calls, flat.** One `ParticleCloud` for fire, one for smoke, shared
 * by every aeroplane in the sky. Sparks ride the fire cloud rather than getting
 * a third: a spark *is* a small fast short-lived flame, it wants the same
 * palette ramp and the same material, and a system nobody can distinguish from
 * another system should not be one.
 *
 * **No per-frame allocation.** The two particle rings, the source buffer and
 * the hit-counter table are all built once in the constructor and mutated in
 * place forever after.
 *
 * ## Where the numbers come from
 *
 * The *states* are recovered and shared with the sprite path — `fire.burning`
 * for the flame, `smoke-trail.ts`'s `smokeIntensity` (i.e. `damage.c`'s 25/137
 * trip) for the trail. The *look* is a port extension and says so: `TUNABLES`
 * below carries every number, and `PARTICLE_RECOVERED_TUNABLES` names the two
 * that are not invented — a puff's 2.52 s life out of `SMOKE.DAT[1]` and its
 * 3 m/s rise, both taken from `smoke-trail.ts` rather than picked a second
 * time, so the two paths dissipate at the same rate and a player flipping the
 * toggle sees the same fight at a different fidelity.
 *
 * ## Hit sparks — what the sim actually offers
 *
 * **There is no impact position.** `combat/collide.ts`'s `RoundImpact` computes
 * an exact `point`, `hit-allocation.ts` drops it on the way to `RoundHit`, and
 * the tick does not return the hits at all — they collapse into `hitsTaken` /
 * `gunHitsTaken` counters inside `settle`. So a spark burst is spawned **at the
 * airframe with scatter**, off the frame-to-frame rise in `gunHitsTaken` — the
 * same counter the painted cockpit ordinals its own bullet holes from
 * (`cockpit-hits.ts`, which has to synthesise a pane for exactly this reason).
 * Threading `point` through `RoundHit` → `RosterTick` → `EncounterState` is the
 * change that would buy real impact points; it is engine surgery and it is not
 * this lane's.
 */

import { emptyRgb, fireRampAt, smokeRampAt, type MutableRgb } from "./fire-palette.ts";
import type { BurningAircraft } from "./frame-signals.ts";
import {
  advanceParticles,
  createParticleSystem,
  fireOpacity,
  lifeFraction,
  liveParticleCount,
  nextSpread,
  nextUnit,
  owedParticles,
  smokeOpacity,
  spawnParticle,
  type Particle,
  type ParticleSystem,
} from "./particles.ts";
import { smokeIntensity } from "./smoke-trail.ts";
import {
  FIRE_SIZE_EXCESS,
  FIRE_SPREAD_M,
  MAX_FIRE_PARTICLES,
  MAX_PARTICLE_SOURCES,
  MAX_SMOKE_PARTICLES,
  PARTICLE_TUNABLES,
  SMOKE_SIZE_EXCESS,
  SMOKE_SPREAD_M,
  sizeFor,
} from "./particle-tunables.ts";

// The register lives next door (`particle-tunables.ts`, split out at aislop's
// 400-line ceiling) and is re-exported here so no consumer moved.
export type { ParticleTunableKey } from "./particle-tunables.ts";
export {
  MAX_FIRE_PARTICLES,
  MAX_PARTICLE_SOURCES,
  MAX_SMOKE_PARTICLES,
  PARTICLE_CALIBRATION_PENDING,
  PARTICLE_RECOVERED_TUNABLES,
  PARTICLE_TUNABLES,
} from "./particle-tunables.ts";

const FIRE_DRAG = 2.5;
const FIRE_RISE_MPS = 2.4;
const SMOKE_DRAG = 1.2;
const SPARK_DRAG = 6;
/** Metres per second squared. A spark is a hot chip of metal and falls; a flame and a puff do not. */
const SPARK_GRAVITY = -9.81;

/**
 * Where the flame is anchored, in body axes: two metres ahead of the pose
 * origin and a little above it, which on every one of the 22 flyable scouts is
 * the engine bay and the first bay of fuselage behind it.
 *
 * One offset for all of them rather than a table. `cockpit-view.ts` needed a
 * per-airframe `EYE_OFFSETS` because a camera an inch out of place is a
 * different picture; a flame is a two-metre ball of light, the scouts' engines
 * sit within a metre of each other along an axis it spans, and the difference
 * is not resolvable. Same call `DEFAULT_EYE_OFFSET` makes for an unknown key.
 */
const ENGINE_FORWARD_M = 2;
const ENGINE_UP_M = 0.2;

/** One aeroplane's `gunHitsTaken` as of last frame — see `sparkHits`. */
interface HitMemory {
  id: string;
  hits: number;
}

/**
 * What a frame hands the effects: the aeroplanes worth drawing for, and how
 * many of the buffer's slots were filled (`collectBurningAircraft`).
 */
export interface ParticleFrame {
  readonly sources: readonly BurningAircraft[];
  readonly count: number;
}

/**
 * Advances both systems over `dt` and spawns this frame's particles.
 *
 * Split from the drawing so the kill camera can re-emit the same instant
 * through a second lens without the fire ageing twice — the arrangement
 * `sprite-effects.ts` gets by being called with `dt = 0`, made explicit here
 * because two callers is exactly when an implicit one starts to bite.
 */
export class ParticleEmitter {
  readonly fire: ParticleSystem = createParticleSystem(MAX_FIRE_PARTICLES, 0x5f_37_21);
  readonly smoke: ParticleSystem = createParticleSystem(MAX_SMOKE_PARTICLES, 0x1a_c9_04);
  /** One slot per aeroplane on the roster, reused every frame. */
  private readonly hitMemory: HitMemory[] = Array.from({ length: MAX_PARTICLE_SOURCES }, () => ({ id: "", hits: 0 }));

  advance(dt: number, frame: ParticleFrame): void {
    advanceParticles(this.fire, dt);
    advanceParticles(this.smoke, dt);
    if (dt <= 0) return;
    for (let index = 0; index < frame.count; index += 1) {
      const source = frame.sources[index];
      if (!source) continue;
      this.sparkHits(source);
      if (source.burning) this.emitFire(source, dt);
      this.emitSmoke(source, dt);
    }
  }

  /**
   * The flame: born at the engine, thrown along the nose with a fraction of the
   * airframe's own speed, jittered, rising as it cools.
   */
  private emitFire(source: BurningAircraft, dt: number): void {
    const owed = owedParticles(this.fire, PARTICLE_TUNABLES.fireRatePerSecond, dt, MAX_FIRE_PARTICLES);
    const inherit = PARTICLE_TUNABLES.fireVelocityInheritance;
    const jitter = PARTICLE_TUNABLES.fireJitterMps;
    for (let spawned = 0; spawned < owed; spawned += 1) {
      // Along the fuselage rather than at one point, so the flame reads as a
      // band of fire over the airframe instead of a ball stuck to its nose.
      const along = nextUnit(this.fire) * ENGINE_FORWARD_M;
      // `spreadScale` is 1 for everything that is two metres across — every
      // airframe, every train — and a building's own half-extent for the one
      // source that is not (`frame-signals.ts`'s `BurningAircraft.spreadScale`).
      const spread = FIRE_SPREAD_M * (source.spreadScale ?? 1);
      spawnParticle(this.fire, {
        x: source.x + source.forwardX * along + source.upX * ENGINE_UP_M + nextSpread(this.fire, spread),
        y: source.y + source.forwardY * along + source.upY * ENGINE_UP_M + nextSpread(this.fire, spread),
        z: source.z + source.forwardZ * along + source.upZ * ENGINE_UP_M + nextSpread(this.fire, spread),
        vx: source.vx * inherit + nextSpread(this.fire, jitter),
        vy: source.vy * inherit + nextSpread(this.fire, jitter),
        vz: source.vz * inherit + nextSpread(this.fire, jitter),
        life: PARTICLE_TUNABLES.fireLifeSeconds,
        radius: sizeFor(PARTICLE_TUNABLES.fireRadiusM, FIRE_SIZE_EXCESS, source.spreadScale),
        growth: sizeFor(PARTICLE_TUNABLES.fireGrowthMps, FIRE_SIZE_EXCESS, source.spreadScale),
        rise: FIRE_RISE_MPS * FIRE_DRAG,
        drag: FIRE_DRAG,
      });
    }
  }

  /** The trail: `smokeIntensity` decides how much, which is `damage.c`'s own ladder — unless the source carries its own plume rate (a working train's coal smoke). */
  private emitSmoke(source: BurningAircraft, dt: number): void {
    const intensity = source.plumeIntensity ?? smokeIntensity(source);
    if (intensity <= 0) return;
    const rate = PARTICLE_TUNABLES.smokeRatePerSecond * intensity;
    const owed = owedParticles(this.smoke, rate, dt, MAX_SMOKE_PARTICLES);
    const inherit = PARTICLE_TUNABLES.smokeVelocityInheritance;
    const jitter = PARTICLE_TUNABLES.smokeJitterMps;
    for (let spawned = 0; spawned < owed; spawned += 1) {
      // Behind the *tail*, not merely behind the origin: a puff born on top of
      // the engine would bury the flame the same aeroplane is wearing, and from
      // the chase camera — which looks up the trail — the nearest smoke wins the
      // depth test every time.
      const behind = -2 - nextUnit(this.smoke) * 2;
      const spread = SMOKE_SPREAD_M * (source.spreadScale ?? 1);
      spawnParticle(this.smoke, {
        x: source.x + source.forwardX * behind + nextSpread(this.smoke, spread),
        y: source.y + source.forwardY * behind + nextSpread(this.smoke, spread),
        z: source.z + source.forwardZ * behind + nextSpread(this.smoke, spread),
        vx: source.vx * inherit + nextSpread(this.smoke, jitter),
        vy: source.vy * inherit + nextSpread(this.smoke, jitter),
        vz: source.vz * inherit + nextSpread(this.smoke, jitter),
        life: PARTICLE_TUNABLES.smokeLifeSeconds,
        radius: sizeFor(PARTICLE_TUNABLES.smokeRadiusM, SMOKE_SIZE_EXCESS, source.spreadScale),
        growth: sizeFor(PARTICLE_TUNABLES.smokeGrowthMps, SMOKE_SIZE_EXCESS, source.spreadScale),
        rise: PARTICLE_TUNABLES.smokeRiseMps * SMOKE_DRAG,
        drag: SMOKE_DRAG,
      });
    }
  }

  /**
   * Sparks the rise in this aeroplane's `gunHitsTaken` since last frame.
   *
   * An aeroplane first seen mid-fight has no remembered count, so its whole
   * running total would spark at once — hundreds of sparks for hits that landed
   * before the camera arrived. `maxSparkHitsPerFrame` caps that to three hits'
   * worth, which also caps a genuine long burst at a rate the cloud can hold.
   */
  private sparkHits(source: BurningAircraft): void {
    const memory = this.rememberedHits(source.id);
    const fresh = Math.min(PARTICLE_TUNABLES.maxSparkHitsPerFrame, Math.max(0, source.gunHitsTaken - memory.hits));
    memory.hits = source.gunHitsTaken;
    for (let hit = 0; hit < fresh; hit += 1) this.spawnSparkBurst(source);
  }

  /** This aeroplane's slot, claiming a free or stalest one the first time it is seen. */
  private rememberedHits(id: string): HitMemory {
    let spare: HitMemory | undefined;
    for (const entry of this.hitMemory) {
      if (entry.id === id) return entry;
      if (entry.id === "") spare ??= entry;
    }
    const slot = spare ?? this.hitMemory[0];
    if (!slot) return { id, hits: 0 };
    slot.id = id;
    // Seeded to zero, not to the current count: `sparkHits`'s cap is what keeps
    // a mid-fight arrival from paying out its whole history at once, and
    // starting from zero means the very first hit after the camera arrives is
    // sparked rather than swallowed.
    slot.hits = 0;
    return slot;
  }

  private spawnSparkBurst(source: BurningAircraft): void {
    const scatter = PARTICLE_TUNABLES.sparkScatterM;
    const speed = PARTICLE_TUNABLES.sparkSpeedMps;
    for (let spark = 0; spark < PARTICLE_TUNABLES.sparksPerHit; spark += 1) {
      spawnParticle(this.fire, {
        x: source.x + nextSpread(this.fire, scatter),
        y: source.y + nextSpread(this.fire, scatter * 0.5),
        z: source.z + nextSpread(this.fire, scatter),
        // Full inheritance: a chip knocked off an aeroplane is travelling with
        // it, which is what makes the burst read as *on* the airframe rather
        // than hanging in the air behind it.
        vx: source.vx + nextSpread(this.fire, speed),
        vy: source.vy + nextSpread(this.fire, speed),
        vz: source.vz + nextSpread(this.fire, speed),
        life: PARTICLE_TUNABLES.sparkLifeSeconds,
        radius: PARTICLE_TUNABLES.sparkRadiusM,
        growth: 0,
        rise: SPARK_GRAVITY,
        drag: SPARK_DRAG,
      });
    }
  }

  /** How many particles are alight right now, for a budget assertion. */
  liveCounts(): { readonly fire: number; readonly smoke: number } {
    return { fire: liveParticleCount(this.fire), smoke: liveParticleCount(this.smoke) };
  }
}

/** What one particle contributes to a cloud this frame — colour, size, coverage. */
export interface ParticleDraw {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly radiusM: number;
  readonly tint: MutableRgb;
  readonly opacity: number;
}

/** Reused for every particle of every frame; the caller must consume it before asking for the next. */
const scratchDraw: { x: number; y: number; z: number; radiusM: number; tint: MutableRgb; opacity: number } = {
  x: 0,
  y: 0,
  z: 0,
  radiusM: 0,
  tint: emptyRgb(),
  opacity: 0,
};

/**
 * Reads one system's particle at `index` into the shared scratch, or returns
 * `undefined` if that slot is dead. `ramp` is the palette sampler and `fade`
 * the opacity curve — the only two things fire and smoke differ by at draw
 * time, which is why this is one function and not two.
 */
export function drawableAt(
  system: ParticleSystem,
  index: number,
  ramp: (t: number, out: MutableRgb) => MutableRgb,
  fade: (particle: Particle) => number,
): ParticleDraw | undefined {
  const particle = system.particles[index];
  if (!particle || particle.age >= particle.life) return undefined;
  const opacity = fade(particle);
  if (opacity <= 0) return undefined;
  scratchDraw.x = particle.x;
  scratchDraw.y = particle.y;
  scratchDraw.z = particle.z;
  scratchDraw.radiusM = particle.radius;
  scratchDraw.opacity = opacity;
  ramp(lifeFraction(particle), scratchDraw.tint);
  return scratchDraw;
}

/** The fire cloud's sampler pair, named so the renderer reads as prose. */
export const FIRE_DRAW = { ramp: fireRampAt, fade: fireOpacity } as const;
/** The smoke cloud's. */
export const SMOKE_DRAW = { ramp: smokeRampAt, fade: smokeOpacity } as const;
